import { ChevronDown } from "lucide-react";
import { Button, Skeleton, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import type { CreativeAdSpendRow, CreativeAdSpendSection } from "@nexus/contracts";

const AD_ID_DOT_CLASS_NAMES = ["bg-muted-foreground/40", "bg-chart-4"] as const;

export interface CreativeAdSpendBreakdownProps {
  variant: "collapsible" | "inline";
  adsCount?: number;
  expanded?: boolean;
  onToggle?: () => void;
  section?: CreativeAdSpendSection;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  formatSpend: (spend: number) => string;
}

export function CreativeAdSpendBreakdown({
  variant,
  adsCount = 0,
  expanded = variant === "inline",
  onToggle,
  section,
  isLoading = false,
  isError = false,
  onRetry,
  formatSpend,
}: CreativeAdSpendBreakdownProps) {
  const { t } = useTranslation();
  const showPanel = variant === "inline" || expanded;

  return (
    <div className="flex flex-col gap-2">
      {variant === "collapsible" ? (
        <Button
          type="button"
          variant="ghost"
          onClick={onToggle}
          aria-expanded={expanded}
          className="h-11 min-h-11 w-full justify-between px-2 text-left text-xs font-medium text-foreground"
        >
          <span>
            {t("explore.creativeAdSpend.toggle", {
              defaultValue: "{{count}} ads",
              count: adsCount,
            })}
          </span>
          <ChevronDown className={`size-4 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </Button>
      ) : null}

      {showPanel ? (
        <CreativeAdSpendPanelBody
          variant={variant}
          section={section}
          isLoading={isLoading}
          isError={isError}
          onRetry={onRetry}
          formatSpend={formatSpend}
        />
      ) : null}
    </div>
  );
}

function CreativeAdSpendPanelBody({
  variant,
  section,
  isLoading,
  isError,
  onRetry,
  formatSpend,
}: {
  variant: "collapsible" | "inline";
  section?: CreativeAdSpendSection;
  isLoading: boolean;
  isError: boolean;
  onRetry?: () => void;
  formatSpend: (spend: number) => string;
}) {
  const { t } = useTranslation();
  const isShare = variant === "inline";

  if (isLoading) {
    return isShare ? (
      <div className="flex flex-col gap-4" data-testid="creative-ad-spend-loading">
        <div className="flex items-end justify-between gap-4">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-8 w-40" />
          </div>
          <Skeleton className="h-3 w-28" />
        </div>
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    ) : (
      <div className="space-y-2 px-2" data-testid="creative-ad-spend-loading">
        <Skeleton className="h-3 w-2/3" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn("flex flex-col gap-2", !isShare && "px-2")}>
        <p className="text-xs text-destructive">
          {t("explore.creativeAdSpend.error", "Couldn’t load Ad ID spend.")}
        </p>
        {onRetry ? (
          <Button type="button" variant="outline" size="sm" onClick={onRetry} className="h-11 min-h-11 w-fit">
            {t("explore.creativeAdSpend.retry", "Retry")}
          </Button>
        ) : null}
      </div>
    );
  }

  const emptyMessage = (
    <p className="text-xs text-muted-foreground">
      {t("explore.creativeAdSpend.empty", "No Ad IDs in this window.")}
    </p>
  );

  if (!section) {
    return <div className={cn(!isShare && "px-2")}>{emptyMessage}</div>;
  }

  const body =
    section.rows.length === 0 ? (
      emptyMessage
    ) : isShare ? (
      <CreativeAdSpendShareTable section={section} formatSpend={formatSpend} />
    ) : (
      <CreativeAdSpendListBody section={section} formatSpend={formatSpend} />
    );

  return (
    <div className={cn("flex flex-col", isShare ? "gap-4" : "gap-2 px-2 pb-1")}>
      <CreativeAdSpendScope section={section} density={isShare ? "comfortable" : "compact"} />
      {body}
    </div>
  );
}

function CreativeAdSpendScope({
  section,
  density,
}: {
  section: CreativeAdSpendSection;
  density: "comfortable" | "compact";
}) {
  const { t } = useTranslation();
  const accountsLabel =
    section.accounts.length > 0
      ? section.accounts.join(", ")
      : t("explore.creativeAdSpend.allAccounts", "All ad accounts in the current filters");

  return (
    <div
      className={cn(
        "flex flex-col text-muted-foreground",
        density === "compact" ? "gap-2 text-[11px]" : "gap-1 text-xs",
      )}
    >
      <p>
        {t("explore.creativeAdSpend.window", "Date window: {{start}} – {{end}}", {
          start: section.window.startDate,
          end: section.window.endDate,
        })}
      </p>
      <p>{t("explore.creativeAdSpend.accounts", "Covers: {{accounts}}", { accounts: accountsLabel })}</p>
    </div>
  );
}

function sortRowsBySpendDesc(rows: CreativeAdSpendRow[]): CreativeAdSpendRow[] {
  return [...rows].sort((a, b) => b.spend - a.spend);
}

function spendFillRatio(spend: number, maxSpend: number): number {
  return maxSpend > 0 ? spend / maxSpend : 0;
}

const SHARE_PERCENT_DECIMALS = 1;

function formatSharePercent(spend: number, total: number): string {
  if (total <= 0) return "0";
  const percent = Number(((spend / total) * 100).toFixed(SHARE_PERCENT_DECIMALS));
  return String(percent);
}

function CreativeAdSpendListBody({
  section,
  formatSpend,
}: {
  section: CreativeAdSpendSection;
  formatSpend: (spend: number) => string;
}) {
  const { t } = useTranslation();
  const rows = sortRowsBySpendDesc(section.rows);
  const total = rows.reduce((sum, row) => sum + row.spend, 0);
  const maxSpend = rows[0]?.spend ?? 0;

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <ul className="flex flex-col gap-1">
          {rows.map((row) => (
            <AdIdSpendRow
              key={row.adId}
              row={row}
              fill={spendFillRatio(row.spend, maxSpend)}
              shareTooltip={t("explore.creativeAdSpend.sharePercentOfTotal", {
                defaultValue: "{{percent}}% of total spend",
                percent: formatSharePercent(row.spend, total),
              })}
              formatSpend={formatSpend}
              density="compact"
            />
          ))}
        </ul>
      </TooltipProvider>
      <p className="flex items-center justify-between border-t border-border pt-1 text-[11px] text-muted-foreground">
        <span>{t("explore.creativeAdSpend.total", "Ad ID total")}</span>
        <span className="tabular-nums">{formatSpend(total)}</span>
      </p>
    </>
  );
}

function CreativeAdSpendShareTable({
  section,
  formatSpend,
}: {
  section: CreativeAdSpendSection;
  formatSpend: (spend: number) => string;
}) {
  const { t } = useTranslation();
  const rows = sortRowsBySpendDesc(section.rows);
  const total = rows.reduce((sum, row) => sum + row.spend, 0);
  const maxSpend = rows[0]?.spend ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="font-mono text-[10px] font-normal uppercase tracking-label text-muted-foreground">
            {t("explore.creativeAdSpend.totalSpend", "Total spend")}
          </p>
          <p className="text-[32px] font-normal leading-none tabular-nums text-foreground">
            {formatSpend(total)}
          </p>
        </div>
        <p className="shrink-0 pb-0.5 text-xs text-muted-foreground">
          {t("explore.creativeAdSpend.acrossIds", {
            defaultValue: "across {{count}} ad IDs",
            count: rows.length,
          })}
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-2">
          <span className="font-mono text-[10px] uppercase tracking-label text-muted-foreground">
            {t("explore.creativeAdSpend.adIdColumn", "Ad ID")}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-label text-muted-foreground">
            {t("explore.creativeAdSpend.spendColumn", "Spend")}
          </span>
        </div>
        <ul className="divide-y divide-border">
          {rows.map((row, index) => (
            <AdIdSpendRow
              key={row.adId}
              row={row}
              fill={spendFillRatio(row.spend, maxSpend)}
              shareLabel={t("explore.creativeAdSpend.sharePercent", {
                defaultValue: "{{percent}}%",
                percent: formatSharePercent(row.spend, total),
              })}
              dotClassName={AD_ID_DOT_CLASS_NAMES[index % AD_ID_DOT_CLASS_NAMES.length]!}
              formatSpend={formatSpend}
            />
          ))}
        </ul>
        <div className="flex items-center justify-between border-t border-border px-3 py-2.5 text-xs text-foreground">
          <span>{t("explore.creativeAdSpend.rowTotal", "Total")}</span>
          <span className="tabular-nums">{formatSpend(total)}</span>
        </div>
      </div>
    </div>
  );
}

function AdIdSpendRow({
  row,
  fill,
  shareLabel,
  shareTooltip,
  dotClassName,
  formatSpend,
  density = "comfortable",
}: {
  row: CreativeAdSpendRow;
  fill: number;
  shareLabel?: string;
  shareTooltip?: string;
  dotClassName?: string;
  formatSpend: (spend: number) => string;
  density?: "comfortable" | "compact";
}) {
  const isCompact = density === "compact";
  const content = (
    <>
      {isCompact || !dotClassName ? (
        <span className="min-w-0 truncate font-medium text-foreground">{row.adId}</span>
      ) : (
        <span className="flex min-w-0 items-center gap-2">
          <span className={cn("size-2 shrink-0 rounded-full", dotClassName)} />
          <span className="min-w-0 truncate font-medium text-foreground">{row.adId}</span>
        </span>
      )}
      <span className="flex shrink-0 items-baseline gap-2">
        {shareLabel ? (
          <span className="tabular-nums text-muted-foreground">{shareLabel}</span>
        ) : null}
        <span className="tabular-nums text-foreground">{formatSpend(row.spend)}</span>
      </span>
    </>
  );
  const rowClassName = cn(
    "relative flex items-center justify-between gap-2 text-xs",
    isCompact ? "w-full px-1.5 text-left" : "px-3 py-2.5",
  );

  return (
    <li
      className={cn(
        "relative isolate overflow-hidden",
        isCompact && "rounded-md border border-border",
      )}
    >
      <span
        aria-hidden
        className="absolute inset-0 origin-left bg-primary/10 transition-transform duration-300 ease-out motion-reduce:transition-none"
        style={{ transform: `scaleX(${fill})` }}
      />
      {shareTooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className={cn(rowClassName, "border-0 bg-transparent py-0")}>
              {content}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">{shareTooltip}</TooltipContent>
        </Tooltip>
      ) : (
        <div className={rowClassName}>{content}</div>
      )}
    </li>
  );
}
